import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMsal } from '@azure/msal-react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import googleLogo from '../assets/google.png';
import microsoftLogo from '../assets/microsoft.png';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa6';
import {
  FaChartLine,
  FaEnvelope,
  FaKey,
  FaLock,
  FaPhoneAlt,
  FaServer,
  FaShieldAlt,
  FaUsers,
} from 'react-icons/fa';
import AlertMessage from '../components/common/AlertMessage';
import API_ROUTES from '../api/apiUri';
import { apiCall } from '../api/apiCall';
import { isBlank, validateEmailAddress } from '../utils/formValidationUtils';
import { buildPasswordLoginFeedback, isInactiveTenantError } from '../utils/authErrorUtils';
import logger from '../utils/logger';

const authSlides = [
  {
    eyebrow: 'Workforce command center',
    title: 'Run smarter shifts with a single secure workspace.',
    description:
      'Plan shifts, manage teams, and review live workforce activity with a calm admin experience built for daily operations.',
    Icon: FaUsers,
    stats: [
      { value: '35k+', label: 'Employees', Icon: FaUsers },
      { value: '120+', label: 'Companies', Icon: FaChartLine },
      { value: 'Live', label: 'Operations', Icon: FaShieldAlt },
    ],
  },
  {
    eyebrow: 'Security assurance',
    title: 'Protected access for every admin session.',
    description:
      'Microsoft 365 sign-in, encrypted sessions, and tenant-aware controls help keep sensitive workforce data protected.',
    Icon: FaShieldAlt,
    stats: [
      { value: 'MFA', label: 'Ready', Icon: FaKey },
      { value: 'SSL', label: 'Protected', Icon: FaLock },
      { value: '99.9%', label: 'Uptime', Icon: FaServer },
    ],
  },
  {
    eyebrow: 'Operational assurance',
    title: 'Hosted for Australian teams.',
    description:
      'Australian hosting, visible system status, compliance alignment, and a current security update keep admins informed.',
    Icon: FaServer,
    stats: [
      { value: 'Live', label: 'Systems', Icon: FaShieldAlt },
      { value: 'AU', label: 'Hosting', Icon: FaServer },
      { value: 'May 2026', label: 'Updated', Icon: FaChartLine },
    ],
  },
];

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { instance } = useMsal();

  // State declarations - MUST be before any function that uses them
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [alert, setAlert] = useState({ open: false, message: '', type: 'error' });
  const [activeSlide, setActiveSlide] = useState(0);
  const currentSlide = authSlides[activeSlide];
  const SlideIcon = currentSlide.Icon;

  useEffect(() => {
    const slideTimer = window.setInterval(() => {
      setActiveSlide((slide) => (slide + 1) % authSlides.length);
    }, 6000);

    return () => window.clearInterval(slideTimer);
  }, []);

  const pick = (...values) => {
    return values.find(
      (value) =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        (typeof value !== 'string' || value.trim() !== '')
    ) || null;
  };

  const normalizeTenants = (tenants) => {
    if (!tenants || !Array.isArray(tenants) || tenants.length === 0) {
      return [];
    }

    return tenants
      .filter(tenant => tenant && typeof tenant === 'object')
      .map((tenant) => {
        const id = pick(tenant?.id, tenant?.tenantId, tenant?.TenantId, tenant?.companyId, tenant?.CompanyId);
        const name = pick(tenant?.name, tenant?.Name, tenant?.tenantName, tenant?.TenantName, tenant?.companyName, tenant?.CompanyName);
        
        if (id) {
          return {
            ...tenant,
            id,
            tenantId: id,
            name: name || `Tenant ${id}`,
            tenantName: name || `Tenant ${id}`,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  // Microsoft login handler
  const handleMicrosoftLogin = async () => {
    try {
      setIsLoading(true);
      setAlert({ open: false, message: '', type: 'error' });

      // Use environment variable for Azure scope
      const azureClientId = process.env.REACT_APP_AZURE_CLIENT_ID;
      if (!azureClientId) {
        throw new Error('Azure Client ID not configured. Please check your environment variables.');
      }

      const loginResponse = await instance.loginPopup({
        scopes: [`api://${azureClientId}/.default`, "openid", "profile", "email"],
      });
      
      if (!loginResponse?.accessToken) {
        throw new Error('No access token received from Microsoft login');
      }

      const msToken = loginResponse.accessToken;
      // Store token in sessionStorage
      sessionStorage.setItem('msalToken', msToken);

      // Call backend to link Microsoft account (first login)
      try {
        const response = await apiCall({
          url: API_ROUTES.employee.employee_microsoft_login,
          method: 'POST',
          data: { email: loginResponse.account?.username || loginResponse.account?.userPrincipalName },
          headers: { 'Content-Type': 'application/json' },
        });
        
        // Ensure we have the expected response structure
        if (response?.token) {
          login(response.token, response.user, response.tenantId, response.tenants,
            response.features ?? response.Features);
          navigate('/dashboard');
        } else {
          throw new Error('Unexpected response structure from backend');
        }
      } catch (apiErr) {
        logger.error('Microsoft login API error:', apiErr);
        setAlert({ open: true, ...buildPasswordLoginFeedback(apiErr) });
      }
    } catch (err) {
      logger.error('Microsoft login error:', err);
      const errorMessage = err?.message || 'Microsoft login encountered an error. Please try again.';
      setAlert({ open: true, message: errorMessage, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const validateLoginFields = () => {
    const newErrors = {};
    const trimmedEmail = email.trim();

    const emailError = validateEmailAddress(trimmedEmail, {
      requiredMessage: 'Email is required.',
      invalidMessage: 'Enter a valid email address.',
    });
    if (emailError) {
      newErrors.email = emailError;
    }

    if (isBlank(password)) {
      newErrors.password = 'Password is required.';
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLoginFields()) {
      return;
    }

    try {
      setIsLoading(true);
      setAlert({ open: false, message: '', type: 'error' });
      const body = { email: email.trim(), password };
      // Perform both API calls in parallel
      const [authRes, employeeRes] = await Promise.allSettled([
        apiCall({
          url: API_ROUTES.auth.exchange_password,
          method: 'POST',
          data: body,
          headers: { 'Content-Type': 'application/json' },
        }),
        apiCall({
          url: API_ROUTES.employee.employee_login,
          method: 'POST',
          data: body,
          headers: { 'Content-Type': 'application/json' },
        }),
      ]);

      const authData = authRes.status === 'fulfilled' ? authRes.value : null;
      const employeeData = employeeRes.status === 'fulfilled' ? employeeRes.value : null;

      // The token exchange can succeed before the tenant-aware employee endpoint
      // rejects an inactive organization. Do not create a short-lived local session
      // in that case; keep the user on the sign-in page with actionable feedback.
      if (employeeRes.status === 'rejected' && isInactiveTenantError(employeeRes.reason)) {
        setAlert({
          open: true,
          ...buildPasswordLoginFeedback(employeeRes.reason),
        });
        return;
      }

      // Validate appToken is a valid non-empty string
      if (!authData?.appToken || typeof authData.appToken !== 'string' || authData.appToken.trim() === '') {
        setAlert({
          open: true,
          ...buildPasswordLoginFeedback(authRes.status === 'rejected' ? authRes.reason : null),
        });
        return;
      }

      // Allow SuperAdmin, Manager, and Admin roles
      const userRole = pick(
        authData.userRole,
        authData.UserRole,
        authData.role,
        authData.Role,
        employeeData?.userRole,
        employeeData?.UserRole,
        employeeData?.role,
        employeeData?.Role,
      );

      if (userRole === 'SuperAdmin' || userRole === 'Manager' || userRole === 'Admin') {
        // Build availableTenants from single tenantId response (backend returns tenantId, not array)
        const tenantId = pick(
          authData.tenantId,
          authData.TenantId,
          employeeData?.tenantId,
          employeeData?.TenantId,
        );

        // Fallback: try to get tenants array from response
        let availableTenants = normalizeTenants(
          pick(
            authData.availableTenants,
            authData.AvailableTenants,
            authData.tenants,
            authData.Tenants,
            employeeData?.availableTenants,
            employeeData?.AvailableTenants,
            employeeData?.tenants,
            employeeData?.Tenants,
          ),
        );

        // If no tenants array but have tenantId, create array from it
        if (availableTenants.length === 0 && tenantId) {
          availableTenants = [
            {
              id: tenantId,
              tenantId: tenantId,
              name: `Tenant ${tenantId}`,
              tenantName: `Tenant ${tenantId}`,
            },
          ];
        }

        // Validate we have at least one tenant
        if (availableTenants.length === 0) {
          setAlert({ open: true, message: 'No company profile found for this account.', type: 'error' });
          return;
        }

        // Get active tenant ID (use provided one or first available)
        const activeTenantId = tenantId || availableTenants[0]?.id;

        // Validate employeeId is non-empty
        const resolvedUserId = pick(
          authData.employeeId,
          authData.EmployeeId,
          employeeData?.employeeId,
          employeeData?.EmployeeId,
        );

        if (!resolvedUserId || String(resolvedUserId).trim() === '') {
          setAlert({ open: true, message: 'Invalid user profile. Please contact support.', type: 'error' });
          return;
        }

        // Use AuthContext login action with tenant data
        login(authData.appToken, {
          email,
          role: userRole,
          id: String(resolvedUserId),
        }, activeTenantId, availableTenants,
        authData.features ?? authData.Features ?? employeeData?.features ?? employeeData?.Features);
        
        logger.info('Login successful', { userRole, activeTenantId, availableTenants });
        
        // Redirect based on role
        if (userRole === 'SuperAdmin') {
          navigate('/super-admin');
        } else {
          // For Manager/Admin: redirect to dashboard (will auto-select first available tenant if needed)
          navigate('/dashboard');
        }
      } else {
        setAlert({ open: true, message: 'Access denied: Only Manager, Admin, and SuperAdmin users can access this panel.', type: 'warning' });
      }
    } catch (error) {
      setAlert({ open: true, ...buildPasswordLoginFeedback(error) });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex h-screen w-full flex-col overflow-hidden bg-slate-950 text-white">
      {/* Alert Message: Fixed Top Right */}
      {alert.open && (
        <div className="fixed right-2 top-10 z-50 sm:right-10">
          <AlertMessage
            title={alert.title || 'Unable to sign in'}
            message={alert.message}
            type={alert.type}
            timeout={0}
            onClose={() => setAlert({ ...alert, open: false })}
          />
        </div>
      )}

      {/* Background */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#07111f_0%,#10223b_44%,#0a1727_100%)]" />
        <div className="absolute inset-0 opacity-[0.11] [background-size:28px_28px] [background-image:linear-gradient(to_right,rgba(226,232,240,0.28)_1px,transparent_1px),linear-gradient(to_bottom,rgba(226,232,240,0.28)_1px,transparent_1px)]" />
        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-cyan-500/10 to-transparent" />
      </div>

      <main className="relative z-10 mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
        <div className="grid w-full flex-1 grid-cols-1 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/70 shadow-2xl shadow-black/30 backdrop-blur lg:grid-cols-[1.05fr_0.95fr]">
          <aside className="relative overflow-hidden border-b border-white/10 bg-[#061126] p-5 sm:p-7 lg:border-b-0 lg:border-r lg:p-9">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_8%,rgba(14,165,233,0.22),transparent_28%),radial-gradient(circle_at_88%_78%,rgba(37,99,235,0.28),transparent_34%),linear-gradient(145deg,#07152d_0%,#081327_48%,#030916_100%)]" />
            <div className="pointer-events-none absolute inset-0 opacity-[0.08] [background-size:36px_36px] [background-image:linear-gradient(to_right,rgba(255,255,255,0.18)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.18)_1px,transparent_1px)] [mask-image:linear-gradient(to_bottom,black,transparent_82%)]" />
            <div className="pointer-events-none absolute -right-28 top-16 h-80 w-80 rounded-full border border-cyan-200/10" />
            <div className="pointer-events-none absolute -right-12 top-32 h-52 w-52 rounded-full border border-blue-300/10" />
            <div className="pointer-events-none absolute bottom-[-8rem] left-[-6rem] h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />

            <div className="relative flex h-full min-h-[27rem] flex-col">
              <header className="flex items-center">
                <div className="brand-logo-wrap inline-flex">
                  <img
                    src={logo}
                    alt="ShiftSmart"
                    className="brand-logo w-32 drop-shadow-xl sm:w-40"
                  />
                </div>
              </header>

              <section
                aria-roledescription="carousel"
                aria-label="ShiftSmart sign in highlights"
                className="flex flex-1 flex-col justify-center py-8 sm:py-10"
              >
                <div className="max-w-xl">
                  <div className="inline-flex items-center gap-2.5 rounded-full border border-cyan-200/15 bg-cyan-300/[0.07] px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-cyan-100">
                    <SlideIcon className="text-cyan-300" aria-hidden="true" />
                    {currentSlide.eyebrow}
                  </div>

                  <h1 className="mt-5 text-3xl font-bold leading-[1.12] tracking-[-0.025em] text-white sm:text-4xl lg:text-[2.65rem]">
                    {currentSlide.title}
                  </h1>
                  <p className="mt-4 max-w-lg text-sm font-medium leading-6 text-slate-300 sm:text-[15px]">
                    {currentSlide.description}
                  </p>

                  <div className="mt-8 grid grid-cols-3 border-y border-white/10 py-5">
                    {currentSlide.stats.map(({ value, label }, index) => (
                      <div
                        key={`${value}-${label}`}
                        className={`${index > 0 ? 'border-l border-white/10 pl-5' : ''} ${index < 2 ? 'pr-5' : ''}`}
                      >
                        <div className="text-xl font-bold leading-none text-white sm:text-2xl">{value}</div>
                        <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 sm:text-[11px]">
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <div className="flex items-end justify-between gap-6">
                <p className="max-w-xs text-xs leading-5 text-slate-400">
                  One secure workspace for every shift, team, and location.
                </p>

                <div className="flex items-center gap-2" aria-label="Choose sign in highlight">
                  {authSlides.map((slide, index) => (
                    <button
                      key={slide.title}
                      type="button"
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        activeSlide === index
                          ? 'w-8 bg-cyan-300 shadow-[0_0_12px_rgba(103,232,249,0.45)]'
                          : 'w-2.5 bg-white/25 hover:bg-white/50'
                      }`}
                      onClick={() => setActiveSlide(index)}
                      aria-label={`Show ${slide.eyebrow}`}
                      aria-current={activeSlide === index ? 'true' : undefined}
                    />
                  ))}
                </div>
              </div>
            </div>
          </aside>

          <section className="flex min-h-0 flex-col justify-center bg-white p-4 text-slate-900 sm:p-6 lg:p-8">
            <div className="mx-auto w-full max-w-md">
            <div className="mb-4 flex items-center justify-center lg:hidden">
              <div className="brand-logo-wrap">
                <img
                  src={logo}
                  alt="ShiftSmart"
                  className="brand-logo w-36 drop-shadow-xl"
                />
              </div>
            </div>

            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-blue-700">Microsoft 365 preferred</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-950 sm:text-[1.7rem]">Sign In</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Use your work credentials to continue.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                <FaShieldAlt aria-hidden="true" />
                Secure login
              </div>
            </div>

            <button
              type="button"
              className="flex min-h-[3rem] w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-500 shadow-sm disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleMicrosoftLogin}
              disabled
              title="Microsoft 365 sign-in coming soon"
            >
              <img src={microsoftLogo} alt="" className="h-5 w-5" />
              Microsoft 365 sign-in coming soon
            </button>

            <div className="my-4 flex items-center gap-3 text-xs text-slate-500">
              <span className="h-px flex-1 bg-slate-200" />
              <span>or sign in with email</span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            <form onSubmit={handleLogin} className="space-y-3" noValidate>
              <div className="space-y-1.5">
                <label htmlFor="login-email" className="text-sm font-medium text-slate-700">Email</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  const nextEmail = e.target.value;
                  setEmail(nextEmail);
                  if (fieldErrors.email) {
                    const emailError = validateEmailAddress(nextEmail, {
                      requiredMessage: 'Email is required.',
                      invalidMessage: 'Enter a valid email address.',
                    });
                    setFieldErrors((prev) => ({ ...prev, email: emailError }));
                  }
                }}
                onBlur={() => {
                  const emailError = validateEmailAddress(email, {
                    requiredMessage: 'Email is required.',
                    invalidMessage: 'Enter a valid email address.',
                  });
                  setFieldErrors((prev) => ({ ...prev, email: emailError }));
                }}
                disabled={isLoading}
                required
              />
              {fieldErrors.email && <p className="text-red-600 text-xs">{fieldErrors.email}</p>}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="login-password" className="text-sm font-medium text-slate-700">Password</label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 pr-10 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) {
                      setFieldErrors((prev) => ({ ...prev, password: '' }));
                    }
                  }}
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                  onClick={() => setShowPassword((prev) => !prev)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </button>
              </div>
              {fieldErrors.password && <p className="text-red-600 text-xs mt-2">{fieldErrors.password}</p>}
            </div>

            <div className="flex justify-end text-xs sm:text-sm">
              <Link to="/forgot-password" className="font-medium text-blue-700 hover:text-blue-800 hover:underline">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-950/30 transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

            <button
              type="button"
              className="mt-2 flex w-full items-center justify-center rounded-lg border border-slate-200 bg-slate-50 py-2 text-xs text-slate-500 disabled:cursor-not-allowed disabled:opacity-70"
              disabled
              title="Google sign-in coming soon"
            >
              <img src={googleLogo} alt="" className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
              Google sign-in coming soon
            </button>

            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                  <FaEnvelope aria-hidden="true" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Need help signing in?</div>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <a href="mailto:support@shiftsmart.com.au" className="flex min-h-[2.25rem] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-700">
                  <FaEnvelope aria-hidden="true" />
                  Email support
                </a>
                <a href="tel:+61280000000" className="flex min-h-[2.25rem] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-700">
                  <FaPhoneAlt aria-hidden="true" />
                  Call support
                </a>
              </div>
            </div>

            <p className="mt-3 text-center text-xs leading-5 text-slate-500 md:text-left">
              By signing in, you agree to ShiftSmart security, privacy, and acceptable use policies.
            </p>
            </div>
          </section>
        </div>
      </main>

      <footer className="relative z-10 w-full shrink-0 px-3 pb-2 text-[11px] text-white/65 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-1.5 border-t border-white/10 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-semibold text-white/80">© 2026 ShiftSmart. All rights reserved.</div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link to="/privacy-policy" className="hover:text-white hover:underline">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-white hover:underline">Terms and Conditions</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
